/**
 * Plan of Record — shared UI primitives.
 *
 * Light-mode, app-native styling (see docs/PLAN_OF_RECORD_DEVIATIONS.md §3 for
 * why this departs from the dark mockup in the handoff briefs). The semantic
 * intent of the brief's accent colors is preserved through the app's tokens:
 *   priority/emphasis → electric-mustard · health → primary (sage) ·
 *   risk/urgency → luminous-coral · structure → granite-gray.
 */
/* eslint-disable react-refresh/only-export-components -- the PoR UI vocabulary
   (pills, accent maps, formatters) deliberately lives beside its components */

import { useState, type ReactNode } from 'react';
import { Check, Link2, X } from 'lucide-react';
import type {
  PorAskDecisionType,
  PorAskStatus,
  PorCommitmentStatus,
  PorKpi,
  PorKpiTier,
  PorRiskSeverity,
  PorSectionStatus,
  PorStatus,
} from '../../types/plan-of-record';

// ─── Card ───────────────────────────────────────────────────────────────────

export function PorCard({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)]  border border-[var(--rule)] dark:border-[var(--rule)] p-6 ${className}`}
    >
      {children}
    </div>
  );
}

export function PorCardHeader({
  title,
  count,
  action,
  hint,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  hint?: string;
}) {
  return (
    <div className="flex items-start justify-between mb-4">
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[var(--text-ter)]">
          {title}
          {typeof count === 'number' && (
            <span className="ml-2 text-[var(--text-ter)] font-mono normal-case tracking-normal">{count}</span>
          )}
        </h3>
        {hint && <p className="text-xs text-[var(--text-ter)] mt-0.5">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────────
// Per the build contract: meaningful empty states, not spinners, not blank
// cards with "0".

export function PorEmptyState({
  icon,
  title,
  prompt,
  action,
}: {
  icon?: ReactNode;
  title: string;
  prompt: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center text-center py-8 px-4 border border-dashed border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)]/60 dark:bg-[color-mix(in_srgb,var(--bg)_80%,transparent)]">
      {icon && <div className="text-[var(--text-ter)] mb-2">{icon}</div>}
      <p className="text-sm font-medium text-[var(--text-sec)]">{title}</p>
      <p className="text-xs text-[var(--text-ter)] mt-1 max-w-sm">{prompt}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

// ─── Pills ──────────────────────────────────────────────────────────────────

function Pill({ label, className }: { label: string; className: string }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5  text-[11px] font-medium font-mono uppercase tracking-wide ${className}`}
    >
      {label}
    </span>
  );
}

const POR_STATUS_STYLES: Record<PorStatus, string> = {
  draft: 'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  in_review: 'bg-electric-mustard-100 text-electric-mustard-900',
  approved: 'bg-primary-50 text-primary-700',
  superseded: 'bg-[var(--hover-tint)] text-[var(--text-ter)] line-through',
};
export function PorStatusPill({ status }: { status: PorStatus }) {
  return <Pill label={status.replace('_', ' ')} className={POR_STATUS_STYLES[status]} />;
}

const SECTION_STATUS_STYLES: Record<PorSectionStatus, string> = {
  draft: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
  in_review: 'bg-electric-mustard-100 text-electric-mustard-900',
  approved: 'bg-primary-50 text-primary-700',
};
export function SectionStatusPill({ status }: { status: PorSectionStatus }) {
  return <Pill label={status.replace('_', ' ')} className={SECTION_STATUS_STYLES[status]} />;
}

const COMMITMENT_STATUS_STYLES: Record<PorCommitmentStatus, string> = {
  // Forest, not the old sage: sage and the slate of `next` both repaint to slate, which made the two pills one.
  active: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  next: 'bg-consulting-slate-blue-100 text-consulting-slate-blue-700',
  blocked: 'bg-luminous-coral-100 text-luminous-coral-900',
  complete: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
  watch: 'bg-electric-mustard-100 text-electric-mustard-900',
};
export function CommitmentStatusPill({ status }: { status: PorCommitmentStatus }) {
  return <Pill label={status} className={COMMITMENT_STATUS_STYLES[status]} />;
}

const SEVERITY_STYLES: Record<PorRiskSeverity, string> = {
  high: 'bg-luminous-coral-100 text-luminous-coral-900',
  medium: 'bg-electric-mustard-100 text-electric-mustard-900',
  low: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
};
export function SeverityPill({ severity }: { severity: PorRiskSeverity }) {
  return <Pill label={severity} className={SEVERITY_STYLES[severity]} />;
}

const ASK_STATUS_STYLES: Record<PorAskStatus, string> = {
  open: 'bg-electric-mustard-100 text-electric-mustard-900',
  approved: 'bg-primary-50 text-primary-700',
  deferred: 'bg-[var(--hover-tint)] text-[var(--text-ter)]',
};
/** What the person sees for an ask's state. The stored key stays `approved`; the act is Accept. */
const ASK_STATUS_LABELS: Record<PorAskStatus, string> = {
  open: 'open',
  approved: 'accepted',
  deferred: 'deferred',
};
export function AskStatusPill({ status }: { status: PorAskStatus }) {
  return <Pill label={ASK_STATUS_LABELS[status] ?? status} className={ASK_STATUS_STYLES[status]} />;
}

/** What each executive-ask decision type is called on screen. The stored key `approve` reads as the act Accept. */
export const ASK_DECISION_TYPE_LABELS: Record<PorAskDecisionType, string> = {
  approve: 'Accept',
  resource: 'Resource',
  authorize: 'Authorize',
  sequence: 'Sequence',
  confirm: 'Confirm',
};

// ─── KPI tier accents ───────────────────────────────────────────────────────

export const KPI_TIER_ACCENT: Record<PorKpiTier, { bar: string; dot: string; label: string }> = {
  // primary → mustard (decisions / lighthouse / critical path)
  primary: { bar: 'bg-electric-mustard-600', dot: 'bg-electric-mustard-600', label: 'text-electric-mustard-900' },
  // health → forest (execution health). Was the old sage, which repaints to the same slate as `watch`.
  health: { bar: 'bg-[var(--green)]', dot: 'bg-[var(--green)]', label: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
  // watch → slate (structure / artifacts / pipeline)
  watch: { bar: 'bg-consulting-slate-blue-500', dot: 'bg-consulting-slate-blue-500', label: 'text-consulting-slate-blue-700' },
};

// ─── Condition accents ──────────────────────────────────────────────────────
// Condition A is the healthy reference case (green). B/C/D are failure modes:
// when inactive they are subdued; when one is the *active* condition it gets a
// prominent coral treatment so the dashboard reads as "we are not OK".

export const CONDITION_ACCENT = {
  // Forest, as the note above says. The edge is an inset shadow because a border color class loses to `.dark *` in Night.
  healthyActive: 'border-transparent shadow-[inset_0_0_0_1px_var(--green)] bg-[var(--green-dim)] text-[var(--text-pri)]',
  failureActive: 'border-luminous-coral-300 bg-luminous-coral-50 text-luminous-coral-900',
  inactive: 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)]',
};

// ─── Buttons ────────────────────────────────────────────────────────────────

export function PrimaryButton({
  children,
  onClick,
  type = 'button',
  disabled,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2  transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  onClick,
  disabled,
  className = '',
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`text-sm font-medium px-3 py-1.5  border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-40 transition-colors ${className}`}
    >
      {children}
    </button>
  );
}

/**
 * "The answer is a URL" — copies the current location to the clipboard with a
 * brief inline confirmation. Used on the shareable read views (Synopsis,
 * Sponsor Brief).
 */
export function CopyLinkButton({ className = '' }: { className?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions / non-secure context) — do nothing.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5  border border-[var(--rule)] dark:border-[var(--rule)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors ${className}`}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5 text-primary-600" /> Copied
        </>
      ) : (
        <>
          <Link2 className="h-3.5 w-3.5" /> Copy link
        </>
      )}
    </button>
  );
}

/**
 * Read-only KPI card for the shareable views. Same visual grammar as the
 * dashboard's KpiGrid cards (tier dot, value, progress bar) minus the edit
 * affordance. Honours "no data ≠ zero".
 */
export function KpiReadout({ kpi }: { kpi: PorKpi }) {
  const accent = KPI_TIER_ACCENT[kpi.tier];
  const display = kpiDisplayValue(kpi.has_data, kpi.value_display);
  const noData = !kpi.has_data;

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] p-4">
      <div className="flex items-center gap-1.5">
        <span className={`h-1.5 w-1.5 rounded-full ${accent.dot}`} />
        <span className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-ter)]">{kpi.tier}</span>
      </div>
      <p className="text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mt-1 leading-snug">{kpi.label}</p>

      <div className="mt-3 flex items-end gap-2">
        <span
          className={`text-2xl font-semibold tabular-nums ${
            noData ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'
          }`}
        >
          {display}
        </span>
        {kpi.target_label && (
          <span className="text-xs text-[var(--text-ter)] mb-1">
            / {kpi.target_label}
            {kpi.target_window ? ` · ${kpi.target_window}` : ''}
          </span>
        )}
      </div>

      <div className="mt-2 h-1.5 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--hover-tint)] overflow-hidden">
        {!noData && kpi.progress_pct != null && (
          <div className={`h-full ${accent.bar}`} style={{ width: `${kpi.progress_pct}%` }} />
        )}
      </div>
      {noData && <p className="text-[11px] text-[var(--text-ter)] mt-1.5">No data yet — not zero.</p>}
    </div>
  );
}

// ─── Modal shell (Pattern A) ────────────────────────────────────────────────

export function PorModal({
  title,
  subtitle,
  onClose,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80">
        <div className="flex items-start justify-between px-6 pt-5 pb-3 border-b border-[var(--rule)]">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-pri)]">{title}</h2>
            {subtitle && <p className="text-xs text-[var(--text-ter)] mt-0.5">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)] -mt-1 -mr-1 p-1">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-4">{children}</div>
        {footer && <div className="px-6 py-4 border-t border-[var(--rule)] flex justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * A modal that captures a required, non-empty rationale before confirming an
 * authority transition. Used for condition changes, ask decisions, and status
 * changes — each of which appends a governance event.
 */
export function RationaleModal({
  title,
  subtitle,
  confirmLabel,
  fieldLabel = 'Rationale',
  placeholder = 'Why is this change being made? This is recorded in the immutable Change Log.',
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  subtitle?: string;
  confirmLabel: string;
  /** Label over the text box. Defaults to "Rationale". */
  fieldLabel?: string;
  /** Placeholder for the text box — say where the words end up. */
  placeholder?: string;
  onConfirm: (rationale: string) => Promise<void> | void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = rationale.trim().length > 0;

  const handleConfirm = async () => {
    if (!valid) {
      setError('A rationale is required — it cannot be saved empty.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(rationale.trim());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong.');
      setBusy(false);
      return;
    }
    setBusy(false);
  };

  return (
    <PorModal
      title={title}
      subtitle={subtitle}
      onClose={onClose}
      footer={
        <>
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleConfirm} disabled={busy || !valid}>
            {busy ? 'Saving…' : confirmLabel}
          </PrimaryButton>
        </>
      }
    >
      {children}
      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5 mt-1">
        {fieldLabel} <span className="text-luminous-coral-700">*</span>
      </label>
      <textarea
        autoFocus
        value={rationale}
        onChange={(e) => {
          setRationale(e.target.value);
          if (error) setError(null);
        }}
        rows={3}
        placeholder={placeholder}
        className="w-full text-sm border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none resize-none"
      />
      {error && <p className="text-xs text-luminous-coral-700 mt-1.5">{error}</p>}
    </PorModal>
  );
}

// ─── Misc ───────────────────────────────────────────────────────────────────

/** Renders a KPI value honouring "no data ≠ zero": "—" when has_data is false. */
export function kpiDisplayValue(hasData: boolean, valueDisplay: string | null): string {
  if (!hasData) return '—';
  return valueDisplay && valueDisplay.trim() ? valueDisplay : '—';
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

export function relativeDays(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  const days = Math.round(diff / 86_400_000);
  if (days === 0) return 'today';
  if (days > 0) return `in ${days}d`;
  return `${Math.abs(days)}d ago`;
}
